import { supabase } from './supabase';
import { FunctionsHttpError } from '@supabase/supabase-js';
import { ChecklistServiceLogger } from '../utils/logger';
import {
  captureSentryExceptionWithScope,
  captureSentryMessageWithScope,
} from './monitoring/sentry';
import { IS_CHECKLIST_RECOVERY_DIAGNOSTIC } from '../config/recoveryDiagnostic';
import { isExpectedWebChecklistShareResolveError } from '@/utils/isExpectedWebChecklistShareResolveError';

const logger = ChecklistServiceLogger.Main;

const DEFAULT_TIMEOUT_MS = 15000;

const assertChecklistShareWriteAllowed = (): void => {
  if (IS_CHECKLIST_RECOVERY_DIAGNOSTIC) {
    throw new Error('Operacao de escrita bloqueada no modo de recuperacao.');
  }
};

export type ChecklistShareLinkStatus = 'active' | 'completed' | 'revoked' | 'expired';
export type ChecklistShareScopeMode = 'template_default' | 'custom_fields';

export interface CreateChecklistShareLinkParams {
  checklistId: string;
  ttlMinutes?: number;
  maxUpdates?: number | null;
  scopeMode?: ChecklistShareScopeMode;
  allowedFieldIds?: string[] | null;
}

export interface ChecklistShareCreateResponse {
  success: boolean;
  share_link_id: string;
  checklist_id: string;
  status: ChecklistShareLinkStatus;
  expires_at: string;
  ttl_minutes: number;
  max_updates: number | null;
  token: string;
  url: string;
  scope_mode: ChecklistShareScopeMode;
  allowed_field_ids: string[] | null;
}

export interface ChecklistShareStatusBody {
  share_link: {
    id: string;
    checklist_id: string;
    status: ChecklistShareLinkStatus;
    expires_at: string;
    expires_in_seconds: number;
    revoked_at: string | null;
    completed_at: string | null;
    updates_count: number;
    max_updates: number | null;
    last_activity_at: string | null;
    created_at: string;
    updated_at: string;
    scope_mode: ChecklistShareScopeMode;
    allowed_field_ids: string[] | null;
  };
}

export interface ChecklistShareRevokeBody {
  share_link: {
    id: string;
    checklist_id: string;
    status: ChecklistShareLinkStatus;
    revoked_at: string | null;
    completed_at: string | null;
    expires_at: string;
    updates_count: number;
    max_updates: number | null;
  };
}

export interface ChecklistShareCompleteBody {
  share_link: {
    id: string;
    checklist_id: string;
    status: ChecklistShareLinkStatus;
    revoked_at: string | null;
    completed_at: string | null;
    expires_at: string;
    updates_count: number;
    max_updates: number | null;
    last_activity_at: string | null;
  };
}

export interface ChecklistShareField {
  id: string;
  grupo: string;
  nome: string;
  tipo: string;
  ordem: number;
  obrigatorio: boolean;
  public_editable: boolean;
}

export interface ChecklistShareResolveResponse {
  success: boolean;
  share_link: {
    id: string;
    status: ChecklistShareLinkStatus;
    expires_at: string;
    expires_in_seconds: number;
    updates_count: number;
    max_updates: number | null;
    last_activity_at: string | null;
    scope_mode: ChecklistShareScopeMode;
    allowed_field_ids: string[] | null;
  };
  empresa?: {
    id: string;
    nome: string;
  } | null;
  checklist: {
    id: string;
    status: string;
    sequencial_numero: number | null;
    sequencial_codigo: string | null;
    template_id: string;
  };
  fields: ChecklistShareField[];
  editable_field_ids: string[];
  dropdown_options: Record<string, string[]>;
  values: Record<string, string>;
}

export interface ChecklistSharePatchResponse {
  success: boolean;
  share_link_id: string;
  checklist_id: string;
  applied_fields: string[];
  updates_count: number;
  max_updates: number | null;
  expires_at: string;
  expires_in_seconds: number;
}

export interface PatchChecklistShareValuesParams {
  token: string;
  changes: Record<string, string | null>;
}

const shortId = (value: string | null | undefined, left = 6, right = 4): string => {
  if (!value) {
    return 'n/a';
  }

  if (value.length <= left + right) {
    return value;
  }

  return `${value.slice(0, left)}...${value.slice(-right)}`;
};

const normalizeErrorMessage = (error: unknown): string => {
  if (error instanceof Error && error.message) {
    return error.message;
  }
  if (typeof error === 'string' && error.trim()) {
    return error.trim();
  }
  return 'Erro desconhecido';
};

const asRecord = (value: unknown): Record<string, unknown> | null => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }
  return value as Record<string, unknown>;
};

const extractInvokeError = async (
  fnName: string,
  error: unknown
): Promise<Error & { statusCode?: number; shareStatus?: string; code?: string; rawMessage?: string }> => {
  const fallbackMessage = normalizeErrorMessage(error);
  const wrapped = new Error(fallbackMessage) as Error & {
    statusCode?: number;
    shareStatus?: string;
    code?: string;
    rawMessage?: string;
  };
  wrapped.rawMessage = fallbackMessage;

  if (!(error instanceof FunctionsHttpError)) {
    return wrapped;
  }

  // Extrai payload de erro customizado da Edge Function (ex.: { error, status }).
  const context = error.context as
    | {
        status?: number;
        clone?: () => { json?: () => Promise<unknown> };
        json?: () => Promise<unknown>;
      }
    | undefined;
  wrapped.statusCode = context?.status;

  try {
    const responseClone = typeof context?.clone === 'function' ? context.clone() : context;
    const parsed = (await responseClone?.json?.()) ?? null;
    const payload = asRecord(parsed);

    if (!payload) {
      return wrapped;
    }

    const apiMessage =
      typeof payload.error === 'string'
        ? payload.error
        : typeof payload.message === 'string'
        ? payload.message
        : null;
    const apiStatus = typeof payload.status === 'string' ? payload.status : null;
    const apiCode = typeof payload.code === 'string' ? payload.code : null;

    if (apiStatus) {
      wrapped.shareStatus = apiStatus;
    }
    if (apiCode) {
      wrapped.code = apiCode;
    }
    if (apiMessage) {
      wrapped.message = apiStatus ? `${apiMessage} (${apiStatus})` : apiMessage;
    }

    logger.warn(`[checklist-share-service] invoke non-2xx fn=${fnName}`, {
      statusCode: wrapped.statusCode ?? null,
      shareStatus: wrapped.shareStatus ?? null,
      code: wrapped.code ?? null,
      message: wrapped.message,
      rawMessage: wrapped.rawMessage,
    });
  } catch (parseError) {
    logger.warn(`[checklist-share-service] invoke non-2xx parse_failed fn=${fnName}`, {
      statusCode: wrapped.statusCode ?? null,
      rawMessage: wrapped.rawMessage,
      parseError: normalizeErrorMessage(parseError),
    });
  }

  return wrapped;
};

const invokeWithTimeout = async <TResponse>(
  fnName: string,
  body: Record<string, unknown>,
  timeoutMs: number = DEFAULT_TIMEOUT_MS
): Promise<TResponse> => {
  logger.info(`[checklist-share-service] invoke init fn=${fnName}`, {
    bodyKeys: Object.keys(body),
    timeoutMs,
  });

  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  const controller = new AbortController();

  const timeoutPromise = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => {
      reject(Object.assign(new Error(`Timeout ao chamar ${fnName} (${timeoutMs}ms)`), { code: 'checklist_share_timeout' }));
      controller.abort();
    }, timeoutMs);
  });

  try {
    const response = await Promise.race([
      supabase.functions.invoke(fnName, { body, signal: controller.signal }),
      timeoutPromise,
    ]);

    const { data, error } = response as { data: TResponse | null; error: { message?: string } | null };

    if (error) {
      throw await extractInvokeError(fnName, error);
    }

    if (!data) {
      throw new Error(`Resposta vazia da função ${fnName}`);
    }

    logger.info(`[checklist-share-service] invoke success fn=${fnName}`);
    return data;
  } finally {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
  }
};

const captureShareError = (
  stage: string,
  error: unknown,
  context: Record<string, unknown>
) => {
  if (error instanceof Error) {
    captureSentryExceptionWithScope(error, {
      level: 'error',
      context: {
        flow: 'checklist_share',
        stage,
        ...context,
      },
      tags: {
        flow: 'checklist_share',
        stage,
      },
      fingerprint: ['checklist_share', stage, error.name, error.message],
      dedupeKey: `checklist_share:${stage}:${error.message}`,
      dedupeWindowMs: 30000,
    });
    return;
  }

  captureSentryMessageWithScope('Falha checklist share com erro não padronizado', {
    level: 'error',
    context: {
      flow: 'checklist_share',
      stage,
      ...context,
      rawError: String(error),
    },
    tags: {
      flow: 'checklist_share',
      stage,
    },
    fingerprint: ['checklist_share', stage, 'non_standard_error'],
    dedupeKey: `checklist_share:${stage}:non_standard_error`,
    dedupeWindowMs: 30000,
  });
};

export const createChecklistShareLink = async (
  params: CreateChecklistShareLinkParams
): Promise<ChecklistShareCreateResponse> => {
  assertChecklistShareWriteAllowed();
  const {
    checklistId,
    ttlMinutes = 20,
    maxUpdates = null,
    scopeMode = 'template_default',
    allowedFieldIds = null,
  } = params;

  logger.info('[checklist-share-service] create init', {
    checklistId: shortId(checklistId),
    ttlMinutes,
    maxUpdates,
    scopeMode,
    allowedFieldIdsCount: Array.isArray(allowedFieldIds) ? allowedFieldIds.length : 0,
  });

  try {
    const payload = await invokeWithTimeout<ChecklistShareCreateResponse>(
      'checklist-share-create',
      {
        checklist_id: checklistId,
        ttl_minutes: ttlMinutes,
        max_updates: maxUpdates,
        scope_mode: scopeMode,
        allowed_field_ids: allowedFieldIds,
      }
    );

    if (!payload.success || !payload.share_link_id || !payload.url) {
      throw new Error('Resposta inválida ao criar link de compartilhamento');
    }

    logger.info('[checklist-share-service] create success', {
      shareLinkId: shortId(payload.share_link_id),
      checklistId: shortId(payload.checklist_id),
      status: payload.status,
      expiresAt: payload.expires_at,
      maxUpdates: payload.max_updates,
      scopeMode: payload.scope_mode,
      allowedFieldIdsCount: Array.isArray(payload.allowed_field_ids) ? payload.allowed_field_ids.length : 0,
    });

    return payload;
  } catch (error) {
    logger.error('[checklist-share-service] create failed', {
      checklistId: shortId(checklistId),
      message: normalizeErrorMessage(error),
    });
    captureShareError('create', error, {
      checklistId,
      ttlMinutes,
      maxUpdates,
      scopeMode,
      allowedFieldIdsCount: Array.isArray(allowedFieldIds) ? allowedFieldIds.length : 0,
    });
    throw error;
  }
};

export const getChecklistShareStatus = async (
  shareLinkId: string
): Promise<ChecklistShareStatusBody> => {
  logger.info('[checklist-share-service] status init', {
    shareLinkId: shortId(shareLinkId),
  });

  try {
    const payload = await invokeWithTimeout<{ success: boolean } & ChecklistShareStatusBody>(
      'checklist-share-status',
      {
        share_link_id: shareLinkId,
      }
    );

    if (!payload.success || !payload.share_link?.id) {
      throw new Error('Resposta inválida ao consultar status de compartilhamento');
    }

    logger.info('[checklist-share-service] status success', {
      shareLinkId: shortId(payload.share_link.id),
      status: payload.share_link.status,
      expiresInSeconds: payload.share_link.expires_in_seconds,
      updatesCount: payload.share_link.updates_count,
      maxUpdates: payload.share_link.max_updates,
    });

    return payload;
  } catch (error) {
    logger.error('[checklist-share-service] status failed', {
      shareLinkId: shortId(shareLinkId),
      message: normalizeErrorMessage(error),
    });
    captureShareError('status', error, {
      shareLinkId,
    });
    throw error;
  }
};

export const revokeChecklistShareLink = async (
  shareLinkId: string
): Promise<ChecklistShareRevokeBody> => {
  assertChecklistShareWriteAllowed();
  logger.info('[checklist-share-service] revoke init', {
    shareLinkId: shortId(shareLinkId),
  });

  try {
    const payload = await invokeWithTimeout<{ success: boolean } & ChecklistShareRevokeBody>(
      'checklist-share-revoke',
      {
        share_link_id: shareLinkId,
      }
    );

    if (!payload.success || !payload.share_link?.id) {
      throw new Error('Resposta inválida ao revogar link de compartilhamento');
    }

    logger.info('[checklist-share-service] revoke success', {
      shareLinkId: shortId(payload.share_link.id),
      status: payload.share_link.status,
      revokedAt: payload.share_link.revoked_at,
    });

    return payload;
  } catch (error) {
    logger.error('[checklist-share-service] revoke failed', {
      shareLinkId: shortId(shareLinkId),
      message: normalizeErrorMessage(error),
    });
    captureShareError('revoke', error, {
      shareLinkId,
    });
    throw error;
  }
};

export const completeChecklistShareByToken = async (
  token: string
): Promise<ChecklistShareCompleteBody> => {
  assertChecklistShareWriteAllowed();
  logger.info('[checklist-share-service] complete init', {
    tokenPreview: shortId(token, 8, 8),
    tokenLength: token.length,
  });

  try {
    const payload = await invokeWithTimeout<{ success: boolean } & ChecklistShareCompleteBody>(
      'checklist-share-complete',
      {
        token,
      }
    );

    if (!payload.success || !payload.share_link?.id) {
      throw new Error('Resposta inválida ao concluir sessão pública');
    }

    logger.info('[checklist-share-service] complete success', {
      shareLinkId: shortId(payload.share_link.id),
      checklistId: shortId(payload.share_link.checklist_id),
      status: payload.share_link.status,
      completedAt: payload.share_link.completed_at,
      updatesCount: payload.share_link.updates_count,
      maxUpdates: payload.share_link.max_updates,
    });

    return payload;
  } catch (error) {
    logger.error('[checklist-share-service] complete failed', {
      tokenPreview: shortId(token, 8, 8),
      message: normalizeErrorMessage(error),
    });
    captureShareError('complete', error, {
      tokenPreview: shortId(token, 8, 8),
      tokenLength: token.length,
    });
    throw error;
  }
};

export const resolveChecklistShareToken = async (
  token: string
): Promise<ChecklistShareResolveResponse> => {
  logger.info('[checklist-share-service] resolve init', {
    tokenPreview: shortId(token, 8, 8),
    tokenLength: token.length,
  });

  try {
    const payload = await invokeWithTimeout<ChecklistShareResolveResponse>(
      'checklist-share-resolve',
      {
        token,
      }
    );

    if (!payload.success || !payload.share_link?.id || !payload.checklist?.id) {
      throw new Error('Resposta inválida ao resolver link de compartilhamento');
    }

    logger.info('[checklist-share-service] resolve success', {
      shareLinkId: shortId(payload.share_link.id),
      checklistId: shortId(payload.checklist.id),
      status: payload.share_link.status,
      fieldsCount: payload.fields?.length || 0,
      editableFieldsCount: payload.editable_field_ids?.length || 0,
    });

    return payload;
  } catch (error) {
    if (isExpectedWebChecklistShareResolveError(error)) {
      logger.info('[checklist-share-service] resolve session ended', {
        message: normalizeErrorMessage(error),
      });
      throw error;
    }
    logger.error('[checklist-share-service] resolve failed', {
      tokenPreview: shortId(token, 8, 8),
      message: normalizeErrorMessage(error),
    });
    captureShareError('resolve', error, {
      tokenPreview: shortId(token, 8, 8),
      tokenLength: token.length,
    });
    throw error;
  }
};

export const patchChecklistShareValues = async (
  params: PatchChecklistShareValuesParams
): Promise<ChecklistSharePatchResponse> => {
  assertChecklistShareWriteAllowed();
  const { token, changes } = params;
  const fieldIds = Object.keys(changes || {});

  logger.info('[checklist-share-service] patch init', {
    tokenPreview: shortId(token, 8, 8),
    fieldsCount: fieldIds.length,
    fieldIdsSample: fieldIds.slice(0, 5),
  });

  try {
    const payload = await invokeWithTimeout<ChecklistSharePatchResponse>(
      'checklist-share-patch',
      {
        token,
        changes,
      }
    );

    if (!payload.success || !payload.share_link_id || !payload.checklist_id) {
      throw new Error('Resposta inválida ao aplicar atualização pública');
    }

    logger.info('[checklist-share-service] patch success', {
      shareLinkId: shortId(payload.share_link_id),
      checklistId: shortId(payload.checklist_id),
      appliedFieldsCount: payload.applied_fields?.length || 0,
      updatesCount: payload.updates_count,
      maxUpdates: payload.max_updates,
      expiresInSeconds: payload.expires_in_seconds,
    });

    return payload;
  } catch (error) {
    logger.error('[checklist-share-service] patch failed', {
      tokenPreview: shortId(token, 8, 8),
      fieldsCount: fieldIds.length,
      message: normalizeErrorMessage(error),
    });
    captureShareError('patch', error, {
      tokenPreview: shortId(token, 8, 8),
      fieldsCount: fieldIds.length,
    });
    throw error;
  }
};
